import { Component, ComponentInterface, Element, Event, EventEmitter, h, Host, JSX, Listen, Prop, State, Watch } from '@stencil/core';
import { transformTagNames } from '../../global/transform-tag-name';
import { SharedDirectionService } from '../../global/init-services';
import { getStepsInRange } from '../../shared/get-steps-in-range';
import { getDataOnFromTheme, WorkbenchTheme } from '../../shared/themes';
import { roundToNearest } from '../../utils/round-to-nearest';
import { clamp } from '../../utils/clamp';

// works like this:
// when an activeInput exists, the active input expands from one end of the component until the value of the other input
// before: [##O###XXXOX]
// after:  [##O######OX]
//
// when no activeInput exists, both inputs are evenly spread across the available space
// before: [##O######OX]
// after:  [##O###XXXOX]
//
// to debug, enable debug styles in scss file :)
@Component({
  tag: 'wb-range-slider',
  styleUrl: 'range-slider.scss',
  shadow: true,
})
export class RangeSliderComponent implements ComponentInterface {
  @Element()
  public el!: HTMLElement;

  /**
   * Colour scheme
   * @deprecated Use `data-on` attribute instead
   * */
  @Prop({ reflect: true })
  public theme?: WorkbenchTheme;

  /** disables the input */
  @Prop({ reflect: true })
  public disabled?: boolean;

  /** the overall component's label, rendered above the range slider */
  @Prop()
  public label!: string;

  /** the start input's label (not visible) */
  @Prop()
  public startLabel!: string;

  /** the end input's label (not visible) */
  @Prop()
  public endLabel!: string;

  /** the input's min attribute */
  @Prop()
  public min: number = 0;

  /** the input's max attribute */
  @Prop()
  public max: number = 100;

  /** the start input's value attribute */
  @Prop({ reflect: true, mutable: true })
  public startValue: number = 25;

  /** the end input's value attribute */
  @Prop({ reflect: true, mutable: true })
  public endValue: number = 75;

  /** the input's step attribute */
  @Prop()
  public step: number = 1;

  /** value to start a highlighted part on the track */
  @Prop()
  public highlightStart?: number;

  /** value to end a highlighted part on the track */
  @Prop()
  public highlightEnd?: number;

  /** number of scale steps */
  @Prop()
  public scaleSteps?: number;

  /** hides the tooltip shown on hover */
  @Prop({ reflect: true })
  public hideTooltip: boolean = false;

  /** function to format the selected value shown in the tooltip, defaults to toLocaleString */
  @Prop()
  public formatTooltipValue?: (value: number) => string = (value) => value.toLocaleString();

  /** function to format each step in the scale, defaults to toLocaleString */
  @Prop()
  public formatScaleStepValue?: (value: number) => string = (value) => value.toLocaleString();

  /** @internal */
  @Prop({ reflect: true, mutable: true })
  public isRtl: boolean = false;

  @State()
  public activeInput: HTMLInputElement | null = null;

  /** emitted when one input changes value */
  @Event()
  public wbinput!: EventEmitter<Array<number>>;

  /** emitted when one input changes value */
  @Event()
  public wbchange!: EventEmitter<Array<number>>;

  /** emitted when one input changes value */
  @Event({ composed: false })
  public change!: EventEmitter<void>;

  /** emitted when one input receives focus */
  @Event()
  public wbfocus!: EventEmitter<void>;

  /** emitted when one input loses focus */
  @Event()
  public wbblur!: EventEmitter<void>;

  @Listen('wbdirchange', { target: 'document' })
  public updateDirection() {
    this.isRtl = SharedDirectionService.isRtl;
  }

  /** Tooltip Start Visibility */
  @State()
  public isTooltipStartVisible: boolean = false;

  /** Tooltip End Visibility */
  @State()
  public isTooltipEndVisible: boolean = false;

  private TagNames = transformTagNames(this, 'wb-range-slider', ['wb-text', 'wb-visually-hidden', 'wb-tooltip']);
  private startInputEl: HTMLInputElement | undefined;
  private endInputEl: HTMLInputElement | undefined;

  public connectedCallback(): void {
    this.updateDirection();
  }

  @Watch('startValue')
  public validateStartValue(newValue: number): void {
    const clamped = clamp(newValue, this.min, this.max);
    if (this.startValue !== clamped) {
      this.startValue = clamped;
    }
  }

  @Watch('endValue')
  public validateEndValue(newValue: number): void {
    const clamped = clamp(newValue, this.min, this.max);
    if (this.endValue !== clamped) {
      this.endValue = clamped;
    }
  }

  public onInteractionStart = (event: any) => {
    this.activeInput = event.target;
  };

  public onInteractionEnd = () => {
    this.activeInput = null;
    this.isTooltipStartVisible = false;
    this.isTooltipEndVisible = false;
  };

  public onInput = (event: any): void => {
    const target = event.target as HTMLInputElement;

    // If already dragging one input, ignore events from the other input.
    // This prevents "jumping" when overlapping inputs steal pointer events.
    if (this.activeInput && target !== this.activeInput) {
      return;
    }

    const value = Number(target.value);

    if (!this.activeInput) {
      this.activeInput = target;
    }

    if (this.activeInput === this.startInputEl) {
      this.startValue = value;
      this.isTooltipStartVisible = true;
    } else if (this.activeInput === this.endInputEl) {
      this.endValue = value;
      this.isTooltipEndVisible = true;
    }

    this.emitInputEvent();
  };

  public onChange = () => {
    this.activeInput = null;
    this.emitChangeEvent();
  };

  public onFocus = (evt: Event): void => {
    this.onInteractionStart(evt);
    if (this.activeInput === this.startInputEl) {
      this.isTooltipStartVisible = true;
    } else if (this.activeInput === this.endInputEl) {
      this.isTooltipEndVisible = true;
    }
    this.wbfocus.emit();
  };

  public onBlur = (): void => {
    this.onInteractionEnd();
    this.wbblur.emit();
  };

  private emitInputEvent(): void {
    this.wbinput.emit(this.getCurrentValues());
  }

  private emitChangeEvent = (): void => {
    this.wbchange.emit(this.getCurrentValues());
    this.change.emit();
  };

  private getCurrentValues(): Array<number> {
    return [this.startValue, this.endValue];
  }

  private getIntersectionPoint(): number {
    if (this.activeInput) {
      if (this.activeInput === this.startInputEl) {
        return Number(this.endValue);
      } else if (this.activeInput === this.endInputEl) {
        return Number(this.startValue);
      }
    }
    const splitValue = this.getSplitValue();
    return Number(roundToNearest(Number(splitValue), this.step));
  }

  private getStartInputWidth(valueRange: number): number {
    if (this.activeInput) {
      const startIndicatorOffsetStartPercentage = ((this.startValue - this.min) / valueRange) * 100;
      const endIndicatorOffsetStartPercentage = ((this.endValue - this.min) / valueRange) * 100;

      if (this.activeInput === this.startInputEl) {
        return endIndicatorOffsetStartPercentage;
      } else if (this.activeInput === this.endInputEl) {
        return startIndicatorOffsetStartPercentage;
      }
    } else {
      const splitValue = this.getSplitValue();
      const startRangePercentage = ((splitValue - this.min) / valueRange) * 100;

      return startRangePercentage;
    }
    return 0;
  }

  private getOverlayStyles(valueRange: number, startIndicatorOffsetStartPercentage: number, endIndicatorOffsetStartPercentage: number) {
    const highlightStartOffsetStartPercentage = (((this.highlightStart ?? this.min) - this.min) / valueRange) * 100;
    const lineWidthPercentage = (((this.highlightEnd ?? this.min) - (this.highlightStart ?? this.min)) / valueRange) * 100;

    if (this.isRtl) {
      return {
        startIndicator: {
          left: 'initial',
          right: `${startIndicatorOffsetStartPercentage}%`,
        },
        endIndicator: {
          left: 'initial',
          right: `${endIndicatorOffsetStartPercentage}%`,
        },
        progressLine: {
          left: `${100 - endIndicatorOffsetStartPercentage}%`,
          right: `${startIndicatorOffsetStartPercentage}%`,
        },
        highlightLine: {
          left: 'initial',
          right: `${highlightStartOffsetStartPercentage}%`,
          width: `${lineWidthPercentage}%`,
        },
      };
    }

    return {
      startIndicator: {
        left: `${startIndicatorOffsetStartPercentage}%`,
        right: 'initial',
      },
      endIndicator: {
        left: `${endIndicatorOffsetStartPercentage}%`,
        right: 'initial',
      },
      progressLine: {
        left: `${startIndicatorOffsetStartPercentage}%`,
        right: `${100 - endIndicatorOffsetStartPercentage}%`,
      },
      highlightLine: {
        left: `${highlightStartOffsetStartPercentage}%`,
        right: 'initial',
        width: `${lineWidthPercentage}%`,
      },
    };
  }

  private getSplitValue(): number {
    return (Number(this.startValue) + Number(this.endValue)) / 2;
  }

  private getValueRange(): number {
    return this.max - this.min;
  }

  private renderScaleDots(): JSX.Element {
    const els: Array<any> = [];

    for (let i = 0; i < (this.scaleSteps ?? 0); i++) {
      els.push(<div class="dot" />);
    }

    return <div class="dots">{els}</div>;
  }

  private renderScaleNumbers(): JSX.Element {
    const { WbText } = this.TagNames;
    const steps = getStepsInRange(this.min, this.max, this.scaleSteps ?? 0);

    return (
      <div class="scale">
        {steps.map((value) => (
          <div class="scale-item">
            <WbText size="m" static>
              {this.formatScaleStepValue?.(value)}
            </WbText>
          </div>
        ))}
      </div>
    );
  }

  private refStartInput = (element?: HTMLInputElement) => (this.startInputEl = element);
  private refEndInput = (element?: HTMLInputElement) => (this.endInputEl = element);

  public render(): JSX.Element {
    const { WbText, WbVisuallyHidden, WbTooltip } = this.TagNames;

    const valueRange = this.getValueRange();
    const startIndicatorOffsetStartPercentage = ((this.startValue - this.min) / valueRange) * 100;
    const endIndicatorOffsetStartPercentage = ((this.endValue - this.min) / valueRange) * 100;
    const overlayStyles = this.getOverlayStyles(valueRange, startIndicatorOffsetStartPercentage, endIndicatorOffsetStartPercentage);
    const startInputWidth = this.getStartInputWidth(valueRange);
    const hasValidScaleSettings = (this.scaleSteps ?? 0) > 2;

    return (
      <Host class="wb-range-slider" data-on={getDataOnFromTheme(this.el, this.theme)}>
        <WbText class="label" size="l" static>
          {this.label}
        </WbText>
        <div class="inner">
          <WbVisuallyHidden>
            <label htmlFor="start">{this.startLabel}</label>
            <label htmlFor="end">{this.endLabel}</label>
          </WbVisuallyHidden>
          <input //
            id="start"
            name="start"
            type="range"
            ref={this.refStartInput}
            class={{ active: !!this.activeInput && this.activeInput === this.startInputEl }}
            onInput={this.onInput}
            onChange={this.onChange}
            onTouchStart={this.onInteractionStart}
            onMouseUp={this.onInteractionEnd}
            onTouchEnd={this.onInteractionEnd}
            onFocus={this.onFocus}
            onBlur={this.onBlur}
            disabled={this.disabled}
            min={this.min}
            max={this.getIntersectionPoint()}
            step={this.step}
            value={this.startValue}
            style={{ width: `${startInputWidth}%` }}
          />
          <input //
            id="end"
            name="end"
            type="range"
            ref={this.refEndInput}
            class={{ active: !!this.activeInput && this.activeInput === this.endInputEl }}
            onInput={this.onInput}
            onChange={this.onChange}
            onTouchStart={this.onInteractionStart}
            onMouseUp={this.onInteractionEnd}
            onTouchEnd={this.onInteractionEnd}
            onFocus={this.onFocus}
            onBlur={this.onBlur}
            disabled={this.disabled}
            min={this.getIntersectionPoint()}
            max={this.max}
            step={this.step}
            value={this.endValue}
            style={{ width: `${100 - startInputWidth}%` }}
          />

          <div class="overlay">
            <div class="progress-line" style={overlayStyles.progressLine} />
            <div class="indicator is-start" style={overlayStyles.startIndicator}>
              <WbTooltip position="top" variant="inline" open={this.isTooltipStartVisible && !this.hideTooltip}>
                <div></div>
                <div slot="content">{this.formatTooltipValue?.(this.startValue)}</div>
              </WbTooltip>
            </div>
            <div class="indicator is-end" style={overlayStyles.endIndicator}>
              <WbTooltip position="top" variant="inline" open={this.isTooltipEndVisible && !this.hideTooltip}>
                <div></div>
                <div slot="content">{this.formatTooltipValue?.(this.endValue)}</div>
              </WbTooltip>
            </div>
            {this.highlightStart && this.highlightEnd && <div class="highlight-area" style={overlayStyles.highlightLine} />}
            {hasValidScaleSettings && this.renderScaleDots()}
          </div>
        </div>
        {hasValidScaleSettings && this.renderScaleNumbers()}
      </Host>
    );
  }
}
